import React, {useEffect, useLayoutEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {Box, Grid, Typography} from '@mui/material'

import {ReactComponent as AccountCompleteImage} from '@progyny/assets/images/illustration-celebration.svg'
import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import {Navigator} from '@progyny/components/Navigator'
import {useHeap} from '@progyny/hooks/useHeap'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {SetupProfileSubProps} from '@progyny/pages/SetUpProfile/types'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

const Complete: React.FC<SetupProfileSubProps> = ({
  onboardingData,
}: SetupProfileSubProps) => {
  const {t} = useTranslation()
  const history = useHistory()
  const user = useMeStore((s) => s.user)
  const membersName = user?.member.preferredGivenName || user?.firstName
  const {sendHeapData} = useHeap()
  const {getPath} = useRoutes()
  const isMobile = useResponsive('down', 'md')

  useEffect(() => {
    const heapTimeoutId = sendHeapData()

    // Note: For menopause journey, data is already saved step by step
    // No need to submit again here as it would cause duplicate mutations
    return () => clearTimeout(heapTimeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    // Redirect away form page if changing journey or migrating account
    if (
      history.location.pathname === '/change-journey/complete' ||
      history.location.pathname === '/migrate-account/complete'
    )
      history.push(getPath(RouteEnum.HOME))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleOnClick() {
    if (onboardingData.isAspiring) {
      history.push(getPath(RouteEnum.ASPIRING_INFO))
    } else if (onboardingData.isEligible) {
      history.push(getPath(RouteEnum.HOME))
    }
  }

  return (
    <Grid container direction={'column'} justifyContent={'center'}>
      <Grid item xs={12} md={9}>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          pb={{xs: MOBILE_STICKY_HEIGHT, md: 0}}
        >
          <Box mb={4}>
            <Typography variant={'h3'} textAlign={'center'}>
              {t('setup_profile_complete_all_set', {
                memberName: membersName,
              })}
            </Typography>
          </Box>
          <Box mb={6}>
            <AccountCompleteImage />
          </Box>
          <Box>
            <Grid container justifyContent={'center'}>
              <Grid item xs={12}>
                <Typography
                  variant={'h6'}
                  fontWeight={400}
                  textAlign='center'
                  lineHeight={1.8}
                >
                  {t(
                    `Your personalized path is ready—let's explore the care, resources, and support designed just for you.`
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
      <MobileStickyActions>
        <Navigator
          label={t('create_account_personalize_journey_let_s_go')}
          showBackButton={false}
          onNextButtonClick={handleOnClick}
          singleCentered
          fullWidth={isMobile}
        />
      </MobileStickyActions>
    </Grid>
  )
}

export default Complete
